import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Container,
  FormControl,
  Divider,
  Card,
  CardContent,
  Pagination,
} from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import CustomButton from "../../components/Custom/CustomButton";
import CommodityBuyerIcon from "../../assets/Icon/CommodityBuyerIcon.png";
import { useAlert } from "../../components/Alert";
import { useNavigate } from "react-router-dom";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import { Autocomplete, TextField } from "@mui/material";

const AttrDetail = ({ title, value }) => {
  return (
    <Typography variant="h6" sx={{ mb: 2 }}>
      <b>{title}</b>: {value}
    </Typography>
  );
};

const BuyerMarketPlace = () => {
  const [data, setData] = useState([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState("");
  const navigate = useNavigate();
  const [showEmptyState, setShowEmptyState] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { showAlert } = useAlert();

  const fetchStateList = async () => {
    try {
      const response = await api.get("/common/states/");
      setStates(response.data);
    } catch (error) {
      console.error("Error fetching States list:", error);
    }
  };

  const fetchCommodityList = async () => {
    try {
      const response = await api.get("/common/commodities/");
      setCommodities(response.data);
    } catch (error) {
      console.log("Error Fetching Commodities list:", error);
    }
  };

  const handleStateChange = (stateId) => {
    setSelectedState(stateId);
  };

  const handleCommodityChange = (commodityId) => {
    setSelectedCommodity(commodityId);
  };

  useEffect(() => {
    fetchStateList();
    fetchCommodityList();
    fetchCommodities();
  }, []);

  const fetchCommodities = (page = 1, state = "", commodity = "") => {
    setLoading(true);
    let url = `/buyer/get-available-commodities/?page=${page}`;

    if (state) {
      url = `${url}&state_id=${state}`;
    }
    if (commodity) {
      url = `${url}&commodity_id=${commodity}`;
    }
    api
      .get(url)
      .then((result) => {
        if (isNotNullOrEmptyArray(result?.data?.results)) {
          const { results, total_pages } = result.data;
          setData(results);
          setTotalPages(total_pages);
          setLoading(false);
          setShowEmptyState(false);
        } else {
          setData([]);
          setLoading(false);
          setShowEmptyState(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching commodities:", error);
        showAlert("error", t("Failed to fetch commodities"));
      });
  };

  const handleCardClick = (crop_id) => {
    navigate(`/view-crop/${crop_id}`);
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          style={{
            height: "94vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontSize: "30px",
              fontWeight: 700,
              marginTop: "10px",
            }}
          >
            {t("Listed Commodities")}
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <Autocomplete
                  id="state-autocomplete"
                  options={states}
                  getOptionLabel={(option) => option.name}
                  value={
                    states.find((state) => state.id === selectedState) || null
                  }
                  onChange={(event, newValue) => {
                    handleStateChange(newValue?.id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("State")}
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <Autocomplete
                  id="commodity-autocomplete"
                  options={commodities}
                  getOptionLabel={(option) => option.name}
                  value={
                    commodities.find(
                      (commodity) => commodity.id === selectedCommodity
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleCommodityChange(newValue?.id || "");
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: 250,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("Commodity")}
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <CustomButton
                onClick={() => {
                  setSelectedState("");
                  setSelectedCommodity("");
                  fetchCommodities();
                }}
                hideIcon
                backgroundColor="black"
              >
                <b>{t("Reset")}</b>
              </CustomButton>
            </Grid>
            <Grid item xs={6}>
              <CustomButton
                onClick={() =>
                  fetchCommodities(page, selectedState, selectedCommodity)
                }
                hideIcon
              >
                <b>{t("Filter")}</b>
              </CustomButton>
            </Grid>
            <Grid item xs={5} sx={{ margin: "16px auto" }}>
              <Divider sx={{ backgroundColor: "#A5A5A5" }} />
            </Grid>
          </Grid>

          {!showEmptyState && (
            <div
              style={{
                overflowY: "auto",
                height: "83%",
                maxHeight: "83%",
              }}
            >
              <>
                {data.map((item) => (
                  <div
                    style={{
                      width: "99%",
                      marginBottom: "16px",
                    }}
                    key={item.id}
                  >
                    <Card>
                      <CardContent
                        sx={{
                          padding: "20px",
                        }}
                      >
                        <AttrDetail
                          title={t("Farmer Name")}
                          value={item.farmer__full_name || "N/A"}
                        />
                        <AttrDetail
                          title={t("Mobile")}
                          value={item.farmer__phone_number || "N/A"}
                        />
                        <AttrDetail
                          title={t("Commodity")}
                          value={item.commodity__name || "N/A"}
                        />
                        <AttrDetail
                          title={t("Quantity(Kgs)")}
                          value={item.estimated_harvest || "N/A"}
                        />
                        <AttrDetail
                          title={t("State")}
                          value={item.farmland__address__state__name || "N/A"}
                        />
                        <AttrDetail
                          title={t("District")}
                          value={
                            item.farmland__address__district__name || "N/A"
                          }
                        />
                        <AttrDetail
                          title={t("Pin code")}
                          value={item.farmland__address__pin_code || "N/A"}
                        />
                        <AttrDetail
                          title={t("Harvest Date")}
                          value={item.estimated_harvest_date || "N/A"}
                        />
                        <CustomButton
                          onClick={() => handleCardClick(item.id)}
                          hideIcon
                        >
                          <b>{t("Trace Crop")}</b>
                        </CustomButton>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </>
              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    margin: "0px 20px 20px 20px",
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(event, value) => {
                      setPage(value);
                      fetchCommodities(value, selectedState, selectedCommodity);
                    }}
                    fullWidth
                  />
                </div>
              )}
            </div>
          )}

          {showEmptyState && (
            <div
              style={{
                height: "66%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  margin: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={CommodityBuyerIcon}
                  alt="No Commodity Available"
                  style={{
                    width: "127px",
                    height: "127px",
                    marginBottom: "10px",
                  }}
                />

                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, textAlign: "center" }}
                >
                  {t("Currently No Commodity Available")}
                </Typography>
              </div>
            </div>
          )}
        </Container>
      </LoaderContainer>
    </>
  );
};

export default BuyerMarketPlace;
